/**
 * THE MAINTENANCE PANEL — a project's recorded years of cover (Projects C6 API, C8 screen).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠ THE OWNER'S RULING IS "ONE YEAR FROM DELIVERY, PREFILLED AND EDITABLE", AND
 *   BOTH HALVES ARE LOAD-BEARING
 * ═══════════════════════════════════════════════════════════════════════════════
 * PREFILLED: the form opens on the server's proposal, so the common case is two clicks.
 * EDITABLE: the proposal is a starting point and never an oracle — cover gets negotiated,
 * delivery dates get corrected, and a year that cannot be moved would be recorded wrong
 * rather than not at all. So every field is typed over, the panel states WHERE the dates
 * came from (`basis`), and nothing recomputes on read: what was saved is what shows.
 *
 * ⚠ THE SERVER OWNS THE STANDARD YEAR. Leaving the expiry untouched sends NO `expiry_date`
 * and the API applies `expiryFor(term_start, 1)` — including the leap-day rule its own unit
 * tests pin. Computing it here would be a second implementation of a calendar question, and
 * the two would disagree on 29 February.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠ `reminder_notice` IS PRINTED, NEVER COMPOSED
 * ═══════════════════════════════════════════════════════════════════════════════
 * A recorded maintenance date that reminds nobody looks exactly like one that does — that
 * is the Option-D failure the whole renewals coupling exists to prevent, and it is the
 * state the imported licence register sits in today. The SERVER derives whether the nightly
 * scan can actually see this term (the country needs a maintenance product) and says so in
 * a sentence; this panel renders that sentence and invents none of its own. A client-side
 * guess would be a claim about a job it cannot observe.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠ MONEY IS RENDERED, NEVER PARSED
 * ═══════════════════════════════════════════════════════════════════════════════
 * `amount` is a registered name in the masking interceptor, so a seat without
 * `amounts.view` receives the literal `'•••'`. Three states, three sentences: a figure,
 * the mask, and `null` — "nobody has priced this year" — which renders as an em dash.
 * Collapsing any pair of those is a lie, and the em dash carries no "hidden" tooltip
 * precisely because nothing is being hidden.
 *
 * ⚠ A MASKED SEAT GETS NO AMOUNT FIELD ON THE FORM. It is not disabled — it is absent
 * (D5), because an input pre-filled with `•••` would either post the bullets back as a
 * price or silently drop a figure the operator could not see.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Alert, SkeletonRows } from '../../components/ui';
import { useToast } from '../inquiries/toast';
import { formatMoney, isMaskedAmount } from '../inquiries/format';
import { formatDateOnly } from '../renewals/format';
import { cn } from '../../lib/cn';
import {
  crfKeys,
  fetchMaintenancePanel,
  recordMaintenanceTerm,
  updateMaintenanceTerm,
} from './api';
import type { MaintenancePanelDto, MaintenanceTermDto } from './api';
import { crfErrorText } from './errors';

/** Where a proposed date came from — said in words, because a bare date reads as fact. */
const BASIS_TEXT: Readonly<Record<'delivery' | 'renewal', string>> = {
  delivery: 'One year from the last deployment.',
  renewal: 'The year after the term already recorded.',
};

export function MaintenancePanel({
  crfId,
  canEdit,
}: {
  crfId: string;
  /** `crf.edit` — the key the WRITE routes require. The read needs only `crf.view`. */
  canEdit: boolean;
}): React.JSX.Element {
  const panel = useQuery({
    queryKey: crfKeys.maintenance(crfId),
    queryFn: ({ signal }) => fetchMaintenancePanel(crfId, signal),
  });

  if (panel.isPending) {
    return (
      <section className="rounded-md border border-line bg-surface p-5">
        <SkeletonRows rows={3} label="Loading the maintenance record" />
      </section>
    );
  }

  if (panel.isError) {
    /*
     * ⚠ NOT AN EMPTY PANEL. "The maintenance record could not be read" and "this project has
     * no maintenance" are different facts, and on this panel the second one is an ACTION
     * ITEM — somebody has to record the year. Rendering a failed read as "none recorded"
     * would send an operator to record a term that may already exist, and the unique key on
     * (crf_id, term_start) would then refuse them for a reason they could not see.
     */
    return (
      <Alert tone="danger" title="The maintenance record could not be read">
        <p>{crfErrorText(panel.error)}</p>
        <button
          type="button"
          onClick={() => {
            void panel.refetch();
          }}
          className="mt-1 font-medium underline underline-offset-2"
        >
          Try again.
        </button>
      </Alert>
    );
  }

  return <MaintenanceBody crfId={crfId} canEdit={canEdit} panel={panel.data} />;
}

function MaintenanceBody({
  crfId,
  canEdit,
  panel,
}: {
  crfId: string;
  canEdit: boolean;
  panel: MaintenancePanelDto;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const settle = (): void => {
    void queryClient.invalidateQueries({ queryKey: crfKeys.maintenance(crfId) });
  };

  return (
    <section
      data-testid="maintenance-panel"
      className="flex flex-col gap-4 rounded-md border border-line bg-surface p-5"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-body font-semibold text-fg">Maintenance</h2>
          <p className="mt-0.5 text-label text-fg-secondary">
            {panel.delivered_on === null
              ? // ⚠ SAID, NOT LEFT BLANK. Nothing has been deployed, so there is no delivery
                // to count a year from — which is a fact about the project, not a gap.
                'Nothing has been deployed yet, so no delivery date is on record.'
              : `Delivered on ${formatDateOnly(panel.delivered_on)}.`}
          </p>
        </div>
        {canEdit && !adding ? (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex h-8 items-center rounded-sm border border-line bg-surface px-3 text-body13 font-medium text-fg hover:bg-subtle"
          >
            Record a term
          </button>
        ) : null}
      </header>

      {/*
        THE SERVER'S SENTENCE, VERBATIM. See the file header — this is the one thing on the
        panel that says whether the date will actually reach anybody.
      */}
      {panel.reminder_notice === null ? null : (
        <Alert tone="warning" title="This date does not remind anybody yet">
          <p data-testid="maintenance-reminder-notice">{panel.reminder_notice}</p>
        </Alert>
      )}

      {panel.terms.length === 0 && !adding ? (
        <p className="text-body13 text-fg-muted">
          No maintenance term has been recorded for this project.
        </p>
      ) : null}

      {panel.terms.length === 0 ? null : (
        <ul className="flex flex-col gap-2">
          {panel.terms.map((term) =>
            editing === term.id ? (
              <li key={term.id}>
                <TermForm
                  submitLabel="Save the term"
                  initial={{
                    term_start: term.term_start,
                    expiry_date: term.expiry_date,
                    amount: isMaskedAmount(term.amount) ? null : term.amount,
                    currency: term.currency,
                    note: term.note,
                  }}
                  amountEditable={!isMaskedAmount(term.amount)}
                  onCancel={() => setEditing(null)}
                  onSubmit={(body) => updateMaintenanceTerm(crfId, term.id, body)}
                  onDone={() => {
                    setEditing(null);
                    settle();
                    toast.push({ text: 'The maintenance term was updated.' });
                  }}
                  onError={(error) => toast.push({ text: crfErrorText(error), tone: 'error' })}
                />
              </li>
            ) : (
              <li key={term.id}>
                <TermRow term={term} canEdit={canEdit} onEdit={() => setEditing(term.id)} />
              </li>
            ),
          )}
        </ul>
      )}

      {adding ? (
        <TermForm
          submitLabel="Record the term"
          /*
           * ⚠ THE PREFILL, AND IT IS THE SERVER'S. `expiry_date` is seeded from the
           * proposal so the operator SEES the year they are about to record — but if they
           * leave it exactly as proposed the form still sends it, which is deliberate: the
           * value on screen is the value stored, and a form that displayed one date and
           * posted another would be the quietest possible defect on a date panel.
           */
          initial={{
            term_start: panel.prefill.term_start ?? '',
            expiry_date: panel.prefill.expiry_date ?? '',
            amount: null,
            currency: null,
            note: null,
          }}
          basisNote={panel.prefill.basis === null ? null : BASIS_TEXT[panel.prefill.basis]}
          amountEditable
          onCancel={() => setAdding(false)}
          onSubmit={(body) =>
            recordMaintenanceTerm(crfId, { ...body, term_start: body.term_start ?? '' })
          }
          onDone={() => {
            setAdding(false);
            settle();
            toast.push({ text: 'The maintenance term was recorded.' });
          }}
          onError={(error) => toast.push({ text: crfErrorText(error), tone: 'error' })}
        />
      ) : null}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── a term ── */

function TermRow({
  term,
  canEdit,
  onEdit,
}: {
  term: MaintenanceTermDto;
  canEdit: boolean;
  onEdit: () => void;
}): React.JSX.Element {
  const masked = isMaskedAmount(term.amount);
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 rounded-sm border border-line px-3 py-2">
      <div className="flex flex-col gap-0.5">
        <span className="text-body13 text-fg">
          {formatDateOnly(term.term_start)} – {formatDateOnly(term.expiry_date)}
        </span>
        {term.note === null || term.note === '' ? null : (
          <span className="text-label text-fg-muted">{term.note}</span>
        )}
      </div>
      <div className="flex items-baseline gap-3">
        {/*
          The masked-cell idiom, verbatim from the register: the mask renders QUIETER than a
          real figure and carries the reason in a `title`; a NULL renders as the em dash
          `formatMoney` already returns for it, and carries no tooltip because nothing is
          being withheld from anybody.
        */}
        <span
          className={cn('tnum text-body13', masked ? 'text-fg-secondary' : 'text-fg')}
          title={masked ? 'Hidden — this account cannot view amounts in this country' : undefined}
        >
          {formatMoney(term.amount, term.currency)}
        </span>
        {canEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="text-body13 font-medium text-indigo hover:underline"
          >
            Edit
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── form ── */

interface TermFields {
  term_start: string;
  expiry_date: string;
  amount: string | null;
  currency: string | null;
  note: string | null;
}

function TermForm({
  submitLabel,
  initial,
  basisNote = null,
  amountEditable,
  onCancel,
  onSubmit,
  onDone,
  onError,
}: {
  submitLabel: string;
  initial: TermFields;
  basisNote?: string | null;
  amountEditable: boolean;
  onCancel: () => void;
  onSubmit: (body: {
    term_start?: string;
    expiry_date?: string;
    amount?: string | null;
    currency?: string | null;
    note?: string | null;
  }) => Promise<MaintenanceTermDto>;
  onDone: () => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const [fields, setFields] = useState<TermFields>(initial);

  const save = useMutation({
    mutationFn: () =>
      onSubmit({
        term_start: fields.term_start === '' ? undefined : fields.term_start,
        expiry_date: fields.expiry_date === '' ? undefined : fields.expiry_date,
        /*
         * ⚠ AN EMPTY AMOUNT SENDS `null`, NOT `''`. "Nobody has priced this year" is a real
         * state the column allows; posting an empty string would make the server's money
         * validator refuse a form the operator filled in correctly.
         *
         * ⚠ AND THE CURRENCY RIDES WITH IT. The server refuses a figure with no currency —
         * "a figure with no currency is not money" — so they are sent as a pair or not at
         * all, rather than letting the operator discover the rule from a 422.
         */
        amount: amountEditable ? nullIfBlank(fields.amount) : undefined,
        currency: amountEditable ? nullIfBlank(fields.currency) : undefined,
        note: nullIfBlank(fields.note),
      }),
    onSuccess: onDone,
    onError,
  });

  return (
    <form
      data-testid="maintenance-form"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
      className="flex flex-col gap-3 rounded-sm border border-line bg-subtle/40 p-3"
    >
      {basisNote === null ? null : (
        <p className="text-label text-fg-secondary" data-testid="maintenance-basis">
          {basisNote} Change either date if the cover starts or ends elsewhere.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field
          id="maintenance-term-start"
          label="Cover starts"
          type="date"
          value={fields.term_start}
          onChange={(value) => setFields((f) => ({ ...f, term_start: value }))}
        />
        <Field
          id="maintenance-expiry"
          label="Cover ends"
          type="date"
          value={fields.expiry_date}
          onChange={(value) => setFields((f) => ({ ...f, expiry_date: value }))}
        />
        {amountEditable ? (
          <>
            <Field
              id="maintenance-amount"
              label="Amount"
              type="text"
              value={fields.amount ?? ''}
              onChange={(value) => setFields((f) => ({ ...f, amount: value }))}
            />
            <Field
              id="maintenance-currency"
              label="Currency"
              type="text"
              value={fields.currency ?? ''}
              onChange={(value) => setFields((f) => ({ ...f, currency: value }))}
            />
          </>
        ) : null}
      </div>

      <Field
        id="maintenance-note"
        label="Note"
        type="text"
        value={fields.note ?? ''}
        onChange={(value) => setFields((f) => ({ ...f, note: value }))}
      />

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={save.isPending || fields.term_start === ''}
          className="inline-flex h-8 items-center rounded-sm bg-indigo px-3 text-body13 font-medium text-fg-inverse hover:bg-indigo/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-body13 text-fg-secondary hover:text-fg"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function nullIfBlank(value: string | null): string | null {
  return value === null || value.trim() === '' ? null : value.trim();
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
}: {
  id: string;
  label: string;
  type: 'date' | 'text';
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-label text-fg-secondary">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
      />
    </div>
  );
}
